import React from "react";
import { cn } from "@/shared/lib/utils";

export enum ButtonVariant {
  Primary = "primary",
  Secondary = "secondary",
  SecondaryOutline = "secondaryOutline",
  Outline = "outline",
  Ghost = "ghost",
  Destructive = "destructive",
  DestructiveOutline = "destructiveOutline",
  Success = "success",
  Breadcrumb = "breadcrumb",
}

export enum ButtonSize {
  Xs = "xs",
  Sm = "sm",
  Md = "md",
  Lg = "lg",
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: React.ReactNode;
  "data-testid"?: string;
}

const variants = {
  primary: "bg-primary-btn text-on-primary hover:bg-primary-btn/90",
  secondary: "bg-secondary text-on-secondary hover:bg-secondary/90",
  secondaryOutline:
    "border border-secondary text-secondary bg-transparent hover:bg-secondary/10",
  outline:
    "border border-border-strong text-text-strong bg-transparent hover:bg-surface-alt",
  ghost: "hover:bg-surface-alt",
  destructive:
    "bg-error text-white hover:bg-error/90",
  destructiveOutline:
    "border border-error text-error bg-transparent hover:bg-error/10",
  success:
    "bg-success text-white hover:bg-success/90",
  breadcrumb: "bg-transparent",
};

const sizes = {
  xs: "h-[32px] px-4 text-sm",
  sm: "h-9 px-3 text-sm",
  md: "h-10 px-4 py-2",
  lg: "h-11 px-8",
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = ButtonVariant.Primary,
      size = ButtonSize.Xs,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        className={cn(
          "inline-flex items-center justify-center rounded-md font-medium transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          variants[variant],
          sizes[size],
          className,
        )}
        {...props}
      >
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";
